import { useEffect, useMemo, useState } from "react";
import { Info, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { detectInAppBrowser, MBLEARN_PUBLIC_URL } from "@/lib/in-app-browser";
import { isNativePlatform } from "@/lib/capacitor-safe";

/**
 * Discreet notice shown on /auth/login when the page is rendered inside a
 * third-party app's embedded browser (Gmail, Google App, Instagram, etc.).
 *
 * Purpose: tell institutional students that, after activating their account,
 * they should sign in directly at www.mblearn.app in a real browser — so they
 * stop depending on the activation email to find the app again.
 *
 * Dismissible per-session via localStorage.
 */

const DISMISS_KEY = "inapp_browser_notice_dismissed";

export const InAppBrowserNotice = () => {
  const { language } = useLanguage();
  const detection = useMemo(() => detectInAppBrowser(), []);
  const [dismissed, setDismissed] = useState(false);

  // Native Capacitor apps run in a WebView but are not an external in-app
  // browser (Gmail, Instagram, etc.). The notice must be suppressed there so
  // login and registration can happen inside the app without redirecting to
  // the public website.
  if (isNativePlatform()) return null;

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    } catch {
      /* ignore */
    }
  }, []);

  if (!detection.isInApp || dismissed) return null;

  const isEN = language === "en";
  // Use the detected source label when meaningful, fall back to a neutral term.
  const appLabel =
    detection.source === "ios-webview" || detection.source === "android-webview"
      ? isEN
        ? "an in-app browser"
        : "un navegador interno"
      : detection.label ?? (isEN ? "an app" : "una app");

  const title = isEN
    ? `Opened from ${appLabel}`
    : `Acceso desde ${appLabel}`;

  const body = isEN
    ? `This page opened from ${appLabel}. After activating your account, sign in directly at ${MBLEARN_PUBLIC_URL}. You won't need to come back to this email.`
    : `Esta página se abrió desde ${appLabel}. Después de activar tu cuenta, entra directamente en ${MBLEARN_PUBLIC_URL}. No necesitarás volver a este correo.`;

  const dismissLabel = isEN ? "Continue" : "Continuar";

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      className="mb-4 rounded-lg border border-border bg-muted/70 backdrop-blur-sm p-4 text-left animate-fade-in"
    >
      <div className="flex items-start gap-3">
        <Info className="h-4 w-4 mt-0.5 text-primary shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {body}
          </p>
          <div className="mt-3">
            <button
              type="button"
              onClick={handleDismiss}
              className="text-xs font-medium text-primary hover:underline"
            >
              {dismissLabel}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label={dismissLabel}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default InAppBrowserNotice;
